import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Download,
  Eye,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  X,
} from "lucide-react";
import {
  targetTypeInfo,
  schemaFormatters,
  normalizeTargetType,
  formatValue,
} from "../../utils/auditUtils";

const FacilityAgentReporting = () => {
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuditCategoryBadge = (module) => {
    // Optionally style by module name, fallback to gray
    const moduleClasses = {
      payment: "bg-blue-100 text-blue-800",
      funding: "bg-purple-100 text-purple-800",
      document: "bg-green-100 text-green-800",
      redemption: "bg-yellow-100 text-yellow-800",
      auth: "bg-red-100 text-red-800",
      system: "bg-gray-100 text-gray-800",
      // add more as needed
    };
    if (!module) return "bg-gray-100 text-gray-800";
    // Try to match by prefix
    const key = Object.keys(moduleClasses).find((k) =>
      module.toLowerCase().startsWith(k)
    );
    return moduleClasses[key] || "bg-gray-100 text-gray-800";
  };

  const getResultBadge = (result) => {
    return result === "SUCCESS"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  // Real audit logs state and pagination
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLimit, setAuditLimit] = useState(20);
  const [auditPagination, setAuditPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [auditExporting, setAuditExporting] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);

  // Helper to format actor (user) display
  const formatActor = (actor) => {
    try {
      let userId = "";
      let email = "";
      if (typeof actor === "object" && actor !== null) {
        userId = actor._id || "";
        email = actor.email || "";
      } else if (typeof actor === "string") {
        userId = actor;
        email = /@/.test(actor) ? actor : "";
      }
      if (userId && email && userId !== email) return `${userId} (${email})`;
      if (email) return email;
      if (userId) return userId;
      return "Unknown User";
    } catch (e) {
      return "Unknown User";
    }
  };

  const exportAuditLogsCsv = async () => {
    if (auditExporting) return;
    setAuditExporting(true);
    try {
      let page = 1;
      const limit = 100; // larger page size for faster export
      const allLogs = [];
      let pages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.get(
          `/api/audit-logs?page=${page}&limit=${limit}&actorRole=facility_agent`
        );
        const items = res.data.items || [];
        const pagination = res.data.pagination || { page, pages: 1 };
        pages = pagination.pages || pages;
        allLogs.push(...items);
        page += 1;
      } while (page <= pages);

      const headers = [
        "Timestamp",
        "User",
        "Action",
        "Category",
        "Result",
        "IP",
        "TargetType",
        "TargetId",
        "TargetJSON",
        "DetailsJSON",
      ];
      const escapeCsv = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        if (/[,"\n]/.test(str)) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };
      const rows = allLogs.map((log) => {
        const timestamp = log.timestamp
          ? new Date(log.timestamp).toISOString()
          : "";
        const user = formatActor(log.actorId);
        const action = log.action || "";
        const category = log.module || "";
        const result = log.status || "";
        const ip = log.ip || log.ipAddress || "";
        const targetTypeRaw = log.targetType || log.target?.type || "";
        const rawTargetId =
          log.targetId !== undefined
            ? log.targetId
            : log.target?.id !== undefined
            ? log.target?.id
            : log.target?._id;
        let targetId = "";
        if (typeof rawTargetId === "object" && rawTargetId !== null) {
          targetId =
            rawTargetId._id ||
            rawTargetId.id ||
            rawTargetId.code ||
            rawTargetId.name ||
            "";
        } else if (typeof rawTargetId !== "undefined") {
          targetId = String(rawTargetId);
        }
        let targetJson = "";
        if (log.target && typeof log.target === "object") {
          try {
            targetJson = JSON.stringify(log.target);
          } catch (e) {
            targetJson = "";
          }
        }
        let detailsJson = "";
        if (log.details && typeof log.details === "object") {
          try {
            detailsJson = JSON.stringify(log.details);
          } catch (e) {
            detailsJson = "";
          }
        }
        return [
          timestamp,
          user,
          action,
          category,
          result,
          ip,
          targetTypeRaw,
          targetId,
          targetJson,
          detailsJson,
        ]
          .map(escapeCsv)
          .join(",");
      });
      const csvContent = [headers.join(","), ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStamp = new Date()
        .toISOString()
        .replace(/[:T]/g, "-")
        .split(".")[0];
      a.href = url;
      a.download = `facility_agent_audit_logs_${dateStamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export audit logs CSV", err);
      alert("Failed to export audit logs. Please try again.");
    } finally {
      setAuditExporting(false);
    }
  };

  useEffect(() => {
    setAuditLoading(true);
    setAuditError(null);
    axios
      .get(
        `/api/audit-logs?page=${auditPage}&limit=${auditLimit}&actorRole=facility_agent`
      )
      .then((res) => {
        setAuditLogs(res.data.items || []);
        setAuditPagination(
          res.data.pagination || {
            page: auditPage,
            limit: auditLimit,
            total: 0,
            pages: 0,
          }
        );
        setAuditLoading(false);
      })
      .catch(() => {
        setAuditError("Failed to load audit logs");
        setAuditLoading(false);
      });
  }, [auditPage, auditLimit]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
              <p className="mt-2 text-sm text-gray-600">
                Access detailed audit logs for facility agent operations.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-dark-gray">
                  System Audit Trail
                </h2>
                <button
                  type="button"
                  disabled={auditLoading || auditExporting}
                  onClick={exportAuditLogsCsv}
                  className="btn-primary"
                >
                  {auditExporting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Export CSV
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Comprehensive log of all facility agent actions and
                administrative activities.
              </p>
              <div className="mt-2 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Actor role: Facility Agent
              </div>
              {auditExporting && (
                <div className="mt-2">
                  <span className="text-xs text-gray-500">
                    Gathering all pages of audit logs...
                  </span>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Timestamp</th>
                    <th className="table-header-cell">User</th>
                    <th className="table-header-cell">Action</th>
                    <th className="table-header-cell">Category</th>
                    <th className="table-header-cell">Result</th>

                    <th className="table-header-cell">Details</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {auditLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8">
                        Loading...
                      </td>
                    </tr>
                  ) : auditError ? (
                    <tr>
                      <td colSpan="7" className="text-center text-red-500 py-8">
                        {auditError}
                      </td>
                    </tr>
                  ) : auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-8">
                        No audit logs found.
                      </td>
                    </tr>
                  ) : (
                    auditLogs.map((log) => (
                      <tr key={log._id || log.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          {formatDateTime(log.timestamp)}
                        </td>
                        <td className="table-cell">
                          {formatActor(log.actorId)}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {log.action}
                            </div>
                            <div className="text-gray-500">
                              {log.details?.description || ""}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAuditCategoryBadge(
                              log.module
                            )}`}
                          >
                            {log.module ? log.module.replace("_", " ") : ""}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultBadge(
                              log.status?.toUpperCase?.() || log.result
                            )}`}
                          >
                            {(log.status?.toUpperCase?.() || log.result) ===
                            "SUCCESS" ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 mr-1" />
                            )}
                            {log.status?.toUpperCase?.() || log.result}
                          </span>
                        </td>

                        <td className="table-cell">
                          <button
                            className="text-primary-600 hover:text-primary-700 p-1"
                            title="View Details"
                            onClick={() => {
                              setSelectedAuditLog(log);
                              setShowAuditModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                  {showAuditModal && selectedAuditLog && (
                    <tr>
                      <td colSpan="7" className="p-0">
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="px-6 py-4 border-b border-gray-200">
                              <div className="flex items-center justify-between">
                                <h2 className="text-lg font-medium text-gray-900">
                                  Audit Log Details
                                </h2>
                                <button
                                  onClick={() => setShowAuditModal(false)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-6 w-6" />
                                </button>
                              </div>
                            </div>
                            <div className="px-6 py-4">
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="font-medium">
                                    Timestamp:
                                  </span>{" "}
                                  {formatDateTime(selectedAuditLog.timestamp)}
                                </div>
                                <div>
                                  <span className="font-medium">User:</span>{" "}
                                  {formatActor(selectedAuditLog.actorId)}
                                </div>
                                <div>
                                  <span className="font-medium">Action:</span>{" "}
                                  {selectedAuditLog.action}
                                </div>
                                <div>
                                  <span className="font-medium">Category:</span>{" "}
                                  {selectedAuditLog.module?.replace("_", " ") ||
                                    ""}
                                </div>
                                <div>
                                  <span className="font-medium">Result:</span>{" "}
                                  {selectedAuditLog.status ||
                                    selectedAuditLog.result}
                                </div>
                                <div>
                                  <span className="font-medium">
                                    IP Address:
                                  </span>{" "}
                                  {selectedAuditLog.ip ||
                                    selectedAuditLog.ipAddress ||
                                    ""}
                                </div>
                                <div>
                                  <span className="font-medium">Details:</span>{" "}
                                  {selectedAuditLog.details?.description || ""}
                                </div>
                                <div>
                                  <span className="font-medium">Target:</span>{" "}
                                  {(() => {
                                    const targetTypeRaw =
                                      selectedAuditLog.targetType ||
                                      selectedAuditLog.target?.type ||
                                      "";
                                    const targetType =
                                      normalizeTargetType(targetTypeRaw);
                                    const rawTargetId =
                                      selectedAuditLog.targetId !== undefined
                                        ? selectedAuditLog.targetId
                                        : selectedAuditLog.target?.id !==
                                          undefined
                                        ? selectedAuditLog.target?.id
                                        : selectedAuditLog.target?._id;
                                    let targetId = "";
                                    if (
                                      typeof rawTargetId === "object" &&
                                      rawTargetId !== null
                                    ) {
                                      targetId =
                                        rawTargetId._id ||
                                        rawTargetId.id ||
                                        rawTargetId.code ||
                                        rawTargetId.name ||
                                        JSON.stringify(rawTargetId);
                                    } else if (
                                      typeof rawTargetId !== "undefined"
                                    ) {
                                      targetId = String(rawTargetId);
                                    }
                                    if (targetType && targetId) {
                                      return (
                                        <>
                                          <span className="font-mono bg-gray-100 px-1 rounded text-xs">
                                            {targetId}
                                          </span>
                                          <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                            {targetType}
                                          </span>
                                        </>
                                      );
                                    } else if (targetId) {
                                      return (
                                        <span className="font-mono bg-gray-100 px-1 rounded text-xs">
                                          {targetId}
                                        </span>
                                      );
                                    } else if (targetType) {
                                      return (
                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                          {targetType}
                                        </span>
                                      );
                                    }
                                    return "N/A";
                                  })()}
                                </div>
                                {(() => {
                                  const targetTypeRaw =
                                    selectedAuditLog.targetType ||
                                    selectedAuditLog.target?.type ||
                                    "";
                                  const targetType =
                                    normalizeTargetType(targetTypeRaw);
                                  const rawTargetId =
                                    selectedAuditLog.targetId !== undefined
                                      ? selectedAuditLog.targetId
                                      : selectedAuditLog.target?.id !==
                                        undefined
                                      ? selectedAuditLog.target?.id
                                      : selectedAuditLog.target?._id;
                                  let targetId = "";
                                  if (
                                    typeof rawTargetId === "object" &&
                                    rawTargetId !== null
                                  ) {
                                    targetId =
                                      rawTargetId._id ||
                                      rawTargetId.id ||
                                      rawTargetId.code ||
                                      rawTargetId.name ||
                                      JSON.stringify(rawTargetId);
                                  } else if (
                                    typeof rawTargetId !== "undefined"
                                  ) {
                                    targetId = String(rawTargetId);
                                  }
                                  if (
                                    targetType &&
                                    targetId &&
                                    targetTypeInfo[targetType]
                                  ) {
                                    return (
                                      <div className="mt-1 text-xs text-gray-600">
                                        <span className="font-semibold">
                                          {targetTypeInfo[targetType].label}:
                                        </span>{" "}
                                        {targetTypeInfo[targetType].description}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                {selectedAuditLog.target && (
                                  <div className="mt-3">
                                    <div className="font-medium text-gray-900">
                                      Target Details
                                    </div>
                                    {(() => {
                                      const t = selectedAuditLog.target;
                                      const targetTypeRaw =
                                        selectedAuditLog.targetType ||
                                        selectedAuditLog.target?.type ||
                                        "";
                                      const targetType =
                                        normalizeTargetType(targetTypeRaw);
                                      if (
                                        targetType &&
                                        schemaFormatters[targetType]
                                      ) {
                                        return schemaFormatters[targetType](t);
                                      }
                                      const exclude = new Set([
                                        "_id",
                                        "__v",
                                        "createdAt",
                                        "updatedAt",
                                        "password",
                                        "secret",
                                        "hash",
                                      ]);
                                      const entries = Object.entries(t).filter(
                                        ([k]) => !exclude.has(k)
                                      );
                                      if (entries.length === 0)
                                        return (
                                          <div className="mt-1 text-xs text-gray-500">
                                            No details available.
                                          </div>
                                        );
                                      return (
                                        <div className="mt-2">
                                          <ul className="list-disc list-inside text-sm text-gray-700">
                                            {entries.map(([key, value]) => (
                                              <li key={key}>
                                                <span className="font-semibold">
                                                  {key}:
                                                </span>{" "}
                                                {formatValue(value)}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                {selectedAuditLog.details &&
                                  Object.keys(selectedAuditLog.details).filter(
                                    (k) => k !== "description"
                                  ).length > 0 && (
                                    <div className="mt-2">
                                      <span className="font-medium">
                                        Additional Details:
                                      </span>
                                      <ul className="list-disc list-inside">
                                        {Object.entries(
                                          selectedAuditLog.details
                                        )
                                          .filter(([k]) => k !== "description")
                                          .map(([key, value]) => (
                                            <li key={key}>
                                              <span className="font-semibold">
                                                {key}:
                                              </span>{" "}
                                              {formatValue(value)}
                                            </li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {auditPagination.total > 0 && (
                  <span>
                    Showing{" "}
                    {Math.min(
                      (auditPagination.page - 1) * auditPagination.limit + 1,
                      auditPagination.total
                    )}{" "}
                    to{" "}
                    {Math.min(
                      auditPagination.page * auditPagination.limit,
                      auditPagination.total
                    )}{" "}
                    of {auditPagination.total} logs
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                  onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                  disabled={auditLoading || auditPagination.page <= 1}
                >
                  Prev
                </button>
                {Array.from({ length: Math.max(1, auditPagination.pages) })
                  .slice(
                    Math.max(0, auditPagination.page - 3),
                    Math.max(0, auditPagination.page - 3) + 5
                  )
                  .map((_, idx) => {
                    const pageNum = Math.max(1, auditPagination.page - 3) + idx;
                    if (pageNum > auditPagination.pages) return null;
                    const isActive = pageNum === auditPagination.page;
                    return (
                      <button
                        key={pageNum}
                        className={`px-3 py-1.5 text-sm rounded border ${
                          isActive
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-700"
                        }`}
                        onClick={() => setAuditPage(pageNum)}
                        disabled={auditLoading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                <button
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                  onClick={() =>
                    setAuditPage((p) =>
                      Math.min(auditPagination.pages || p, p + 1)
                    )
                  }
                  disabled={
                    auditLoading ||
                    (auditPagination.pages > 0 &&
                      auditPagination.page >= auditPagination.pages)
                  }
                >
                  Next
                </button>
                <select
                  className="ml-2 px-2 py-1 text-sm border border-gray-300 rounded"
                  value={auditLimit}
                  onChange={(e) => {
                    setAuditPage(1);
                    setAuditLimit(Number(e.target.value));
                  }}
                  disabled={auditLoading}
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}/page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacilityAgentReporting;
